import React, {useEffect, useState} from "react";
import {SmcnCertService} from "app/services/rp/SmcnCertService";
import {ResponseResultCode} from "app/model/response-api";
import { numberFormatter } from "app/utils/stringUtil";
import {ScnroModel} from "app/model/rp/ScnroModel";
import {RdmpCondService} from "app/services/rp/RdmpCondService";
import DashboardLoading from "./DashboardLoading";
import DashboardNoData from "./DashboardNoData";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";

export default function ImplementResult(props: Readonly<DashboardModuleProps>) {
  const [sumSmcnCert, setSumSmcnCert] = useState<any>({"1": 0, "2": 0, "3": 0, "4": 0, "5": 0});
  const [latestScnro, setLatestScnro] = useState<ScnroModel | undefined>();
  const [fulfillmentRates, setFulfillmentRates] = useState<any>({"1": 0, "2": 0, "3": 0, "4": 0, "5": 0});
  const [isLoading, setIsLoading] = useState(true);
  const [existsLastScnro, setExistsLastScnro] = useState<boolean>(false);

  useEffect(() => {
    getLatestScnro();
    getAllSumSmcnCert();
  }, []);

  useEffect(() => {
    if (latestScnro && sumSmcnCert) {
      calculateFulfillmentRates();
    }
  }, [latestScnro, sumSmcnCert]);

  async function getLatestScnro() {
    const response = await RdmpCondService.getInstance().getRegistScnro()
    if (response) {
      if (ResponseResultCode.Y === response.resultCode) {
        setLatestScnro(response.data);
        setExistsLastScnro(response.data?.scnroId != null);
      } else {
        setLatestScnro({});
      }
    }
  }

  function getAllSumSmcnCert() {
    let currentDate = new Date();
    SmcnCertService.getInstance().getSumEach(currentDate.getFullYear().toString()).then(response => {
      if (response && ResponseResultCode.Y === response.resultCode) {
        let sum = 0;
        setSumSmcnCert(response.data);
        for (let key in response.data) {
          if (response.data.hasOwnProperty(key)) {
            sum += response.data[key];
          }
        }
      }
    }).finally(() => setIsLoading(false));
  }

  function calculateFulfillmentRates() {
    const rates: any = {
      '1': 0,
      '2': 0,
      '3': 0,
      '4': 0,
      '5': 0
    };
    latestScnro?.scnroInfoList?.forEach((info: any) => {
      const code = info.emksFuflMeansCd.toString();
      const goalQnt = info.fuflMeansGoalQnt;
      if (goalQnt && goalQnt > 0) {
        if (code === '6') {
          // 코드 6일 경우 4와 5 각각의 fulfillmentRates 계산
          rates['4'] = sumSmcnCert['4'] && sumSmcnCert['4'] !== 0 ? ((sumSmcnCert['4'] / goalQnt) * 100).toFixed(2) : 0;
          rates['5'] = sumSmcnCert['5'] && sumSmcnCert['5'] !== 0 ? ((sumSmcnCert['5'] / goalQnt) * 100).toFixed(2) : 0;
        } else {
          rates[code] = sumSmcnCert[code] && sumSmcnCert[code] !== 0 ? ((sumSmcnCert[code] / goalQnt) * 100).toFixed(2) : 0;
        }
      }
    });
    setFulfillmentRates(rates);
  }

  return (
    <DashboardCard
      title="이행실적"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/rp/smcnCert"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      {!isLoading && !existsLastScnro ? (
        <DashboardNoData
          message="등록된 RE100 포트폴리오가 없습니다."
          link={{text: '포트폴리오 등록하기', to: '/rp/portfolio'}}
          className="ex-data2"
        />
      ) : (
        <div className="lst-box-wrap">
          <div className="list-box">
            <p className="tit">자가발전</p>
            <div>
              <p className="tit2">재생에너지 사용량</p>
              <span><strong>{numberFormatter((sumSmcnCert[1] ?? 0), 2)}</strong>kWh</span>
            </div>
            <div>
              <p className="tit2">온실가스 감축량</p>
              <span><strong>{numberFormatter((0.4747 * 0.001 * (sumSmcnCert[1] ?? 0)), 2)}</strong>tCO₂</span>
            </div>
            <div>
              <p className="tit2">이행현황</p>
              <span><strong>{fulfillmentRates[1] ?? 0}</strong>%</span>
            </div>
          </div>

          <div className="list-box">
            <p className="tit">REC</p>
            <div>
              <p className="tit2">재생에너지 사용량</p>
              <span><strong>{numberFormatter((sumSmcnCert[2] ?? 0), 2)}</strong>kWh</span>
            </div>
            <div>
              <p className="tit2">온실가스 감축량</p>
              <span><strong>{numberFormatter((0.4747 * 0.001 * (sumSmcnCert[2] ?? 0)), 2)}</strong>tCO₂</span>
            </div>
            <div>
              <p className="tit2">이행현황</p>
              <span><strong>{fulfillmentRates[2] ?? 0}</strong>%</span>
            </div>
          </div>
          <div className="list-box">
            <p className="tit">녹색프리미엄</p>
            <div>
              <p className="tit2">재생에너지 사용량</p>
              <span><strong>{numberFormatter((sumSmcnCert[3] ?? 0), 2)}</strong>kWh</span>
            </div>
            <div>
              <p className="tit2">온실가스 감축량</p>
              <span><strong>0</strong>tCO₂</span>
            </div>
            <div>
              <p className="tit2">이행현황</p>
              <span><strong>{fulfillmentRates[3] ?? 0}</strong>%</span>
            </div>
          </div>
          <div className="list-box">
            <p className="tit">제3자 PPA</p>
            <div>
              <p className="tit2">재생에너지 사용량</p>
              <span><strong>{numberFormatter((sumSmcnCert[5] ?? 0), 2)}</strong>kWh</span>
            </div>
            <div>
              <p className="tit2">온실가스 감축량</p>
              <span><strong>{numberFormatter((0.4747 * 0.001 * (sumSmcnCert[5] ?? 0)), 2)}</strong>tCO₂</span>
            </div>
            <div>
              <p className="tit2">이행현황</p>
              <span><strong>{fulfillmentRates[5] ?? 0}</strong>%</span>
            </div>
          </div>
          <div className="list-box">
            <p className="tit">직접 PPA</p>
            <div>
              <p className="tit2">재생에너지 사용량</p>
              <span><strong>{numberFormatter((sumSmcnCert[4] ?? 0), 2)}</strong>kWh</span>
            </div>
            <div>
              <p className="tit2">온실가스 감축량</p>
              <span><strong>{numberFormatter((0.4747 * 0.001 * (sumSmcnCert[4] ?? 0)), 2)}</strong>tCO₂</span>
            </div>
            <div>
              <p className="tit2">이행현황</p>
              <span><strong>{fulfillmentRates[4] ?? 0}</strong>%</span>
            </div>
          </div>
        </div>
      )}
    </DashboardCard>
  )
}